import React, { useState } from 'react';
import PropTypes from 'prop-types';
import IntlTelInput from 'react-intl-tel-input';
import { ErrorMessage, useField } from 'formik';
import 'react-intl-tel-input/dist/main.css';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { selectPortalGatewayISOCodesListToIgnoreAreaCode } from '../../store/selectors/settingsSelectors';
import { shouldIgnoreAreaCodeHelper } from '../../util/helpers/phoneNumberHelpers';
import {
  InputError,
  InputHeader,
  InputLabel,
} from './Components/InputComponents';
import { uOutlineNone } from '../../assets/styles/utility';
import { pxToRem } from '../../assets/styles/helper';
import { variables } from '../../assets/styles/variables';
import themeColors from '../../assets/styles/themeColors';
import chevronDown from '../../assets/images/svg/blue-filled-chevron-down.svg';

const getPhoneNumberFieldWrapBorderStyle = (isFocused, isError) => {
  if (isFocused) {
    return `1px solid ${themeColors.colorInputBorderFocused}`;
  }

  if (isError) {
    return `1px solid ${themeColors.colorError}`;
  }

  return `1px solid ${themeColors.colorInputBorder}`;
};

const PhoneNumberFieldWrap = styled.div`
  .intl-tel-input {
    width: 100%;

    input {
      ${uOutlineNone}
      width: 100%;
      height: ${pxToRem(50)};
      border-radius: ${variables.borderRadius.borderRadius};
      border: ${({ isFocused, isError }) =>
        getPhoneNumberFieldWrapBorderStyle(isFocused, isError)};
      padding-left: ${pxToRem(90)} !important;
      font-size: ${pxToRem(16)};
      color: ${themeColors.colorLabel};
      line-height: 1.75;
    }

    .selected-flag {
      border-right: ${({ isFocused, isError }) =>
        getPhoneNumberFieldWrapBorderStyle(isFocused, isError)};
      width: ${pxToRem(80)} !important;
      background-color: transparent !important;

      .iti-flag {
        &::after {
          content: '';
          display: block;
          width: ${pxToRem(16)};
          height: ${pxToRem(16)};
          background-image: url(${chevronDown});
          color: ${themeColors.colorTextPrimary};
          margin-left: ${pxToRem(30)};
          margin-top: -${1};
          transition: all 0.2s;
        }
      }
    }

    .arrow {
      display: none;
    }

    .country-list {
      width: 100%;
      margin-top: 1px;

      li {
        cursor: pointer;
      }

      span {
        font-size: ${pxToRem(16)};
        color: ${themeColors.colorTextSecondary};
        margin-left: ${pxToRem(12)};
      }
    }
  }
`;

const TelephoneInput = ({
  name,
  label,
  onPhoneChange,
  isUsa,
  clearOnCountryChange,
  defaultCountry,
  ...props
}) => {
  const [field, meta] = useField(name);
  const [isFocused, setIsFocused] = useState(false);
  // Opt-in (clearOnCountryChange): switching the country flag clears the field
  // so the newly selected country's example number shows as the placeholder
  // (matching the registration phone field) instead of keeping the previous
  // number. The widget is uncontrolled, so remount it empty (bump the key) with
  // the new country to reset both the value and the auto-placeholder.
  const [instanceKey, setInstanceKey] = useState(0);
  const [clearedValue, setClearedValue] = useState(null);
  const [countryOverride, setCountryOverride] = useState(null);
  // IntlTelInput is uncontrolled — it reads defaultValue/defaultCountry once
  // at mount to pick the initial flag, then AGAIN once its async
  // libphonenumber utils finish loading. If defaultValue tracked
  // field.value reactively, the mount-time onPhoneNumberChange notify
  // (which re-formats the number and calls setFieldValue) would change
  // field.value between those two reads, and the second read — now
  // starting with a dial code it didn't have before — makes the widget
  // silently re-derive the country from digits instead of honoring
  // defaultCountry (e.g. resolving a Puerto-Rico-exclusive area code to
  // "pr" even though a different country was explicitly selected). Freeze
  // the value the field had at mount so it's never fed back into itself.
  const [initialFieldValue] = useState(() => field.value);
  const selectedISOCodesListToIgnoreAreaCode = useSelector(
    selectPortalGatewayISOCodesListToIgnoreAreaCode,
  );

  // process number into string with area code for submission
  const processNumber = (isValid, phone, country) => {
    const shouldIgnoreAreaCode = shouldIgnoreAreaCodeHelper(
      country,
      selectedISOCodesListToIgnoreAreaCode,
    );

    if (shouldIgnoreAreaCode) {
      if (country.iso2 === 'pr') {
        return `+${country.dialCode}${phone}`;
      }

      const phoneNumber = `+${country.dialCode}${phone.substring(3)}`;
      return phoneNumber;
    }

    if (!phone) {
      return '';
    }

    if (phone.includes(`+${country.dialCode}`)) {
      return `${phone}`;
    }

    return `+${country.dialCode}${phone}`;
  };

  return (
    <PhoneNumberFieldWrap
      isFocused={isFocused}
      isError={meta.error && meta.touched}
    >
      <InputHeader>
        {label && <InputLabel htmlFor={field.name}>{label}</InputLabel>}
      </InputHeader>{' '}
      <IntlTelInput
        {...props}
        key={instanceKey}
        label="telephone"
        defaultValue={clearedValue !== null ? clearedValue : initialFieldValue}
        defaultCountry={countryOverride || defaultCountry}
        fieldId={name}
        fieldName={name}
        type="number"
        onPhoneNumberFocus={() => {
          setIsFocused(true);
        }}
        onPhoneNumberBlur={() => {
          setIsFocused(false);
        }}
        onPhoneNumberChange={(isValid, phone, country) => {
          if (!country?.iso2) return;
          onPhoneChange(processNumber(isValid, phone, country), country);
        }}
        onSelectFlag={(phone, country, fullNumber, isValid) => {
          if (!country?.iso2) return;
          // Opt-in: clear the number on country change so the new country's
          // example placeholder shows (remount empty with the new country).
          if (clearOnCountryChange) {
            onPhoneChange('', country);
            setClearedValue('');
            setCountryOverride(country.iso2);
            setInstanceKey((key) => key + 1);
            return;
          }
          onPhoneChange(processNumber(isValid, phone, country), country);
        }}
      />
      <ErrorMessage name={name}>
        {(errorMessage) => {
          const isErrorString = typeof errorMessage === 'string';

          return (
            <InputError>
              {isErrorString ? errorMessage : errorMessage.value}
            </InputError>
          );
        }}
      </ErrorMessage>
    </PhoneNumberFieldWrap>
  );
};

TelephoneInput.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  onPhoneChange: PropTypes.func,
  isUsa: PropTypes.bool,
  clearOnCountryChange: PropTypes.bool,
  defaultCountry: PropTypes.string,
};
export default TelephoneInput;
